/* eslint-disable complexity */
import { css, html } from 'lit';

import { calculateRemainder } from '../../controllers/charges/neb-charges-spreadsheet-controller-util';
import NebTableSpreadsheet, {
  ELEMENTS as BASE_ELEMENTS,
} from '../neb-table-spreadsheet';

export const ELEMENTS = {
  ...BASE_ELEMENTS,
};

class NebTableEraEobCharges extends NebTableSpreadsheet {
  static get styles() {
    return [
      super.styles,
      css`
        /* ERA/EOB specific highlighting styles */
        .cell.highlight-remainder-error,
        .cell.highlight-remainder-error.odd {
          background-color: #f8d7da;
          color: #c62828;
        }

        /* Prevent hover color changes on remainder error cells */
        .row:hover .cell.highlight-remainder-error,
        .row:hover .cell.highlight-remainder-error.odd {
          background-color: #f8d7da !important;
          color: #c62828 !important;
        }

        .cell.highlight-outstanding-error,
        .cell.highlight-outstanding-error.odd {
          background-color: #f8d7da !important;
          color: #c62828 !important;
        }

        .row.row-highlight-remainder-warning .cell:not(.readonly) {
          background-color: #fff3cd;
        }
      `,
    ];
  }

  __initState() {
    super.__initState();

    this.onRemainderChange = () => {};
  }

  // Override getCellClassNames to add ERA/EOB specific highlighting
  getCellClassNames(
    isEditing,
    isEdited,
    isReadOnly,
    isOdd,
    isPopup,
    item,
    key,
  ) {
    const baseClasses = super.getCellClassNames(
      isEditing,
      isEdited,
      isReadOnly,
      isOdd,
      isPopup,
    );

    // Add ERA/EOB specific highlighting for remainder column
    if (key === 'remainder') {
      const actualValue = this.getCellValue(item, key);

      if (actualValue !== 0) {
        return `${baseClasses} highlight-remainder-error`;
      }
    }

    if (key === 'outstanding') {
      const actualValue = this.getCellValue(item, key);

      if (actualValue < 0) {
        return `${baseClasses} highlight-outstanding-error`;
      }
    }

    return baseClasses;
  }

  updateItem(item, field, value) {
    const processedValue = this.processEditedValue(item, field, value);

    // Only update if the value has actually changed
    let valueChanged = false;

    if (processedValue !== item[field]) {
      // Update the edited cells tracking
      if (!this.__editedCells[item.id]) {
        this.__editedCells[item.id] = {};
      }
      this.__editedCells[item.id][field] = processedValue;
      valueChanged = true;
    }

    // Only dispatch event if values actually changed
    if (valueChanged) {
      const updatedItem = {
        ...item,
        [field]: processedValue,
      };

      const newRemainder = calculateRemainder(updatedItem);
      this.onRemainderChange(updatedItem, newRemainder !== 0);
      this.onUpdate(updatedItem, field, value);
    }
  }

  // Override renderCell to pass additional parameters for highlighting
  // eslint-disable-next-line complexity
  renderCell(item, key, index) {
    const isEditing =
      this.__activeCell &&
      this.__activeCell.id === item.id &&
      this.__activeCell.field === key;

    const isEdited =
      this.__editedCells[item.id] &&
      this.__editedCells[item.id][key] !== undefined;

    const configItem = this.config.find(c => c.key === key);
    const isReadOnly =
      configItem?.editable === false || configItem?.getReadOnly?.(item);
    const isPopup = configItem?.popup === true;
    const isForcePopup = configItem?.getForcePopup?.(item);
    const allowNegative = configItem?.allowNegative === true;

    // Use getCellValue to get the actual value (including calculated values)
    const actualValue = this.getCellValue(item, key);
    const displayValue = configItem?.numeric
      ? this.formatNumericValue(key, actualValue)
      : actualValue;

    // Call our specialized getCellClassNames with additional parameters
    const classNames = this.getCellClassNames(
      isEditing,
      isEdited,
      isReadOnly,
      item.isOdd,
      isPopup,
      item,
      key,
    );

    // Get the column index from the config
    const colIndex = this.config.findIndex(c => c.key === key);

    // Custom tooltip for remainder cells with errors
    let tooltipText = displayValue;

    if (key === 'remainder') {
      const remainderValue = this.getCellValue(item, key);

      if (remainderValue !== 0) {
        tooltipText = 'The sum of all yellow columns must = Billed Amount';
      }
    }

    if (key === 'outstanding') {
      const outstandingValue = this.getCellValue(item, key);

      if (outstandingValue < 0) {
        tooltipText = 'Paid amount is greater than debit amount';
      }
    }

    return html`
      <div
        id="${ELEMENTS.cells.get(index, colIndex)}"
        class="${classNames}"
        title="${tooltipText}"
        @click="${e => {
          if (isForcePopup) {
            this.handleExpandClick(e, item, key, isReadOnly);
          } else {
            this.handleCellClick(item, key, isReadOnly);
          }
        }}"
      >
        ${isEditing
          ? this.renderCellInput(
              item,
              key,
              displayValue,
              index,
              colIndex,
              isForcePopup,
              isReadOnly,
              allowNegative,
            )
          : displayValue}
        ${isPopup && !isReadOnly
          ? html`
              <div
                class="expand-icon-container"
                tabindex="0"
                @click="${e =>
                  this.handleExpandClick(e, item, key, isReadOnly)}"
                @keydown="${e =>
                  this.handleExpandIconKeydown(e, item, key, isReadOnly)}"
                title="Open popup"
              >
                <neb-icon class="expand-icon" icon="neb:expand" />
              </div>
            `
          : ''}
      </div>
    `;
  }

  // Override renderRow to add row-level highlighting
  renderRow(item, position, index) {
    // Check if remainder is not 0 for row highlighting
    const remainder = calculateRemainder(item);

    const rowClasses = remainder
      ? 'row row-highlight-remainder-warning'
      : 'row';

    return html`
      <div
        id="row-${index}"
        class="${rowClasses}"
        style="transform: translateY(${position}px)"
      >
        ${this.config.map(configItem =>
          this.renderCell(item, configItem.key, index),
        )}
      </div>
    `;
  }
}

customElements.define('neb-table-era-eob-charges', NebTableEraEobCharges);
